'use client'

import { FC } from 'react'
import { useGetTranslations } from '@/root/shared-hooks'
import { Modal } from '@/root/components/shared/Modal/Modal'
import { useSessionProlong } from '@/root/modules/shared/EditReservation/hooks/useSessionProlong'
import { testIds } from '@/root/shared-consts'

interface SessionProlongModalProps {
  isOpen: boolean
  expireAt: string
  reservationToken: string
  locale: string
  onClose: () => void
  onSessionExtended: (newExpireAt: string) => void
  onEndEditing: () => void
  onExpiredRedirect: () => void
  onError: (message: string, error?: unknown) => void
}

export const SessionProlongModal: FC<SessionProlongModalProps> = ({
  isOpen,
  expireAt,
  reservationToken,
  locale,
  onClose,
  onSessionExtended,
  onEndEditing,
  onExpiredRedirect,
  onError,
}) => {
  const { getTranslation } = useGetTranslations()
  const { isExpired, isEnding, isProlonging, isBusy, prolongSession, endEditing } = useSessionProlong({
    expireAt,
    getTranslation,
    isOpen,
    locale,
    onClose,
    onEndEditing,
    onError,
    onExpiredRedirect,
    onSessionExtended,
    reservationToken,
  })

  return (
    <Modal isOpen={isOpen} onClose={isExpired ? onExpiredRedirect : onClose} hideCloseButton disableDismiss>
      <div className="flex flex-col items-center gap-4">
        <p className="text-neutral-darkest text-center text-lg font-semibold">
          {getTranslation(isExpired ? 'sessionExpiredHeader' : 'editSessionProlongTitle')}
        </p>
        <p className="text-neutral-darker text-center text-sm">
          {getTranslation(isExpired ? 'sessionExpired' : 'editSessionProlongBody')}
        </p>
        <div className="flex w-full gap-4 pt-2">
          {isExpired ? (
            <button
              data-testid={testIds.edit.sessionExpiredOk}
              onClick={onExpiredRedirect}
              className="bg-base-medium-light flex-1 rounded-lg px-6 py-3 font-medium text-white transition-colors hover:opacity-90"
              type="button"
            >
              {getTranslation('modalOk')}
            </button>
          ) : (
            <>
              <button
                data-testid={testIds.edit.sessionEnd}
                onClick={endEditing}
                disabled={isBusy}
                className="border-neutral-medium-light text-neutral-darkest flex-1 rounded-lg border px-6 py-3 font-medium transition-colors hover:bg-gray-50 disabled:opacity-50"
                type="button"
              >
                {isEnding ? getTranslation('editSessionProlongEnding') : getTranslation('editSessionProlongEnd')}
              </button>
              <button
                data-testid={testIds.edit.sessionProlong}
                onClick={prolongSession}
                disabled={isBusy}
                className="bg-base-medium-light flex-1 rounded-lg px-6 py-3 font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
                type="button"
              >
                {isProlonging
                  ? getTranslation('editSessionProlongExtending')
                  : getTranslation('editSessionProlongExtend')}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
